import type { I18NData } from '#root/api/clients/utilities/data-contracts.js'
import type { CountryCode, LocaleCode } from '#types/locale'

/**
 * Address Validation Rules Store - manages country/locale-specific address validation rules and formatting.
 *
 * **Key responsibilities:**
 * - Fetch and cache address validation rules per country/locale
 * - Provide field validation metadata (required, minLength, maxLength)
 * - Manage country list for dropdowns
 * - Determine address validator provider (e.g., Loqate, AddressDoctor)
 *
 * **Use cases:**
 * - Billing/shipping address forms
 * - Checkout address validation
 * - Account address management
 * - Multi-region address format differences
 *
 * **Multi-region/locale behavior:**
 * - Rules vary by country (US vs. UK vs. CA vs. EU)
 * - Locale affects field labels and validation messages
 * - NORA (North America) region has special handling for shipping lists
 * - Address validator provider may differ by region
 *
 * **Caching strategy:**
 * - Rules stored in sessionStorage (survives page refresh)
 * - Keyed by `{countryCode}-{locale}` (e.g., `us-en_US`, `uk-en_GB`)
 * - Lazy loaded on first access per country/locale pair
 *
 * **Performance notes:**
 * - API calls only made once per country/locale per session
 * - Default validation rules applied immediately (required, maxLength)
 * - API response merges with defaults (additive, not replacement)
 *
 * **Multi-brand notes:**
 * - All brands use same address validation infrastructure
 * - Validation rules returned by backend (brand-agnostic)
 * - Country list may vary by brand/region configuration
 *
 * @see domains/commerce/components/form/address for form integration
 * @see domains/commerce/components/checkout for checkout usage
 * @see api/clients/utilities for i18N API contract
 */
export const useAddressRulesStore = defineStore('addressRules', () => {
  const { utilities } = useApi()
  const isNORA = useRegion() === 'NORA'

  /**
   * Cached validation rules per country/locale.
   *
   * **Structure:**
   * ```ts
   * {
   *   'us-en_US': {
   *     billing: { addressLine1: { validation: { required: true, maxLength: 30 } }, ... },
   *     shipping: { addressLine1: { validation: { required: true, maxLength: 30 } }, ... },
   *     billingAddressValidatorProvider: 'LOQATE',
   *     shippingAddressValidatorProvider: 'LOQATE'
   *   },
   *   'uk-en_GB': { ... }
   * }
   * ```
   *
   * **Storage:** sessionStorage (survives page refresh, not browser restart)
   */
  const rules = useSessionStorage('address-rules', {} as Partial<Record<
    `${CountryCode}-${LocaleCode}`,
    {
      billing: any
      shipping: any
      billingAddressValidatorProvider: I18NData[0]['addressValidatorProvider']
      shippingAddressValidatorProvider: I18NData[0]['addressValidatorProvider']
    }
  >>)

  /**
   * List of countries for dropdown selection.
   * **Format:** `[{ label: 'United States', value: 'US' }, ...]`
   * **Storage:** sessionStorage (shared across all locales)
   */
  const countries = useSessionStorage<{ label: string, value: string }[]>('country-list', [])

  /**
   * Default field validation rules (applied before API response).
   * **Used as fallback** if API fails or returns incomplete data.
   * @returns Default validation config for all address fields
   */
  const defaultFields = () => ({
    addressLine1: { validation: { required: true, maxLength: 30 } },
    addressLine2: { validation: { required: false, maxLength: 30 } },
    city: { validation: { required: true, maxLength: 30 } },
    country: { validation: { required: true } },
    email: { validation: { required: true } },
    firstName: { validation: { required: true, minLength: 2, maxLength: 30 } },
    lastName: { validation: { required: true, minLength: 2, maxLength: 30 } },
    phone: { validation: { required: true } },
    postalCode: { validation: { required: true } },
    province: { validation: { required: false } }
  })

  /**
   * Default rules structure for both billing and shipping.
   * @returns Default rules object with billing and shipping fields
   */
  const defaultRules = () =>
    ({ shipping: { ...defaultFields() }, billing: { ...defaultFields() } })

  /**
   * Fetch and cache address validation rules for a specific country/locale.
   *
   * **Behavior:**
   * - Returns immediately if rules already cached for this country/locale
   * - Makes API call to fetch validation metadata
   * - Merges API response with default rules
   * - Extracts address validator provider (e.g., Loqate, AddressDoctor)
   * - NORA region: re-fetches if shipping list is missing
   *
   * **API response format:**
   * - Array of forms (billingForm, shippingForm)
   * - Each form has fields array with validation metadata
   * - Each field has fieldId, validation rules, and other metadata
   *
   * **Side effect:** Also fetches country list if not already loaded
   *
   * @param {string} countryCode - Two-letter country code (lowercase, e.g., 'us', 'uk', 'ca')
   * @param {string} locale - Locale string (e.g., 'en_US', 'en_GB', 'fr_FR')
   *
   * @example
   * ```ts
   * const addressRules = useAddressRulesStore()
   * await addressRules.get('us', 'en_US')
   * // Rules now cached in addressRules.rules['us-en_US']
   * ```
   *
   * ⚠️ **Important:** countryCode is converted to lowercase internally
   */
  const get = (countryCode: string, locale: string) => {
    countryCode = countryCode.toLocaleLowerCase()

    const localeKey = `${countryCode}-${locale}`
    if (!rules.value[localeKey] || (isNORA && !rules.value[localeKey].shipping?.list?.length)) {
      utilities.$i18N(countryCode, {
        locale
      }).then((rulesResp) => {
        rules.value[localeKey] = defaultRules()
        for (const form_ of rulesResp) {
          rules.value[localeKey][`${form_.formId.replace('Form', '')}AddressValidatorProvider`] = form_.addressValidatorProvider
          for (const field of form_.fields) {
            rules.value[localeKey][form_.formId.replace('Form', '')][field.fieldId] = {
              ...field,
              validation: {
                ...rules.value[localeKey][form_.formId.replace('Form', '')][field.fieldId].validation,
                ...field.validation
              }
            }
          }
        }
      })
    }
    if (!countries.value.length) {
      utilities.$countryList({ locale }).then((list) => {
        countries.value = list.map(({ name, code }) => ({ label: name, value: code }))
      })
    }
  }

  return {
    get, // Fetch validation rules for country/locale
    countries, // List of countries for dropdown (sessionStorage cached)
    rules, // Cached validation rules per country/locale

    /**
     * Get the address validator provider for a specific country/locale/approach.
     *
     * **Validator providers:**
     * - LOQATE: Address autocomplete/verification service
     * - ADDRESS_DOCTOR: Alternative address validation service
     * - May return undefined if rules are not yet loaded
     *
     * @param countryCode - Two-letter country code (lowercase)
     * @param locale - Locale string
     * @param approach - Address type
     * @returns Validator provider name
     *
     * @example
     * ```ts
     * const provider = addressRules.getAddressValidatorProvider('us', 'en_US', 'shipping')
     * // Returns: 'LOQATE' or 'ADDRESS_DOCTOR' or undefined
     * ```
     */
    getAddressValidatorProvider: (countryCode: CountryCode, locale: LocaleCode, approach: 'billing' | 'shipping') =>
      rules.value[`${countryCode}-${locale}`]?.[`${approach}AddressValidatorProvider`]
  }
})
